import type { ComponentType } from "react";
import { Activity, ArrowLeft, Building2, Calendar, MapPin } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { PropertySwitcherSearch } from "./PropertySwitcherSearch";
import { type Property, type PropertyListItem } from "./types";

type HeaderSpec = {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string | null | undefined;
};

interface PropertyDetailHeaderProps {
  property: Property;
  clientLogoUrl?: string | null;
  clientName?: string;
  filteredProperties: PropertyListItem[];
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onPropertySwitch: (propertyId: string) => void;
  onBackToClient: () => void;
  headerSpecs: HeaderSpec[];
}

export function PropertyDetailHeader({
  property,
  clientLogoUrl,
  clientName,
  filteredProperties,
  searchTerm,
  onSearchTermChange,
  onPropertySwitch,
  onBackToClient,
  headerSpecs,
}: PropertyDetailHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        {/* Client Logo */}
        <div className="w-16 h-16 flex-shrink-0 rounded-xl bg-muted flex items-center justify-center border-2 border-border shadow-card overflow-hidden">
          {clientLogoUrl ? (
            <img
              src={clientLogoUrl}
              alt={clientName ?? ""}
              className="w-full h-full object-cover"
            />
          ) : (
            <Building2 className="h-8 w-8 text-muted-foreground" />
          )}
        </div>

        {/* Property Info */}
        <div className="min-w-0 space-y-2">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="min-w-0 truncate text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
              {property.name}
            </h1>

            <PropertySwitcherSearch
              currentPropertyId={property.id}
              filteredProperties={filteredProperties}
              searchTerm={searchTerm}
              onSearchTermChange={onSearchTermChange}
              onSelect={onPropertySwitch}
            />
          </div>

          {/* Address */}
          <div className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{property.address}</span>
          </div>

          {/* Metrics Row */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Activity className="h-3 w-3" />
              {property.asset_count} Assets
            </span>
            {property.survey_date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Surveyed {property.survey_date}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="hidden items-center gap-2 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={onBackToClient}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Exit Full-View
        </Button>
      </div>

      {/* Header specs — right-aligned cards, vertically centered with the title block. */}
      <div className="self-center flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
        {headerSpecs.map(({ icon: Icon, label, value }) => (
          <div
            key={label}
            className="flex flex-col items-start gap-0.5 rounded-md border border-border/50 bg-background/80 px-3 py-1.5 min-w-[88px]"
          >
            <div className="flex items-center gap-1">
              <Icon className="h-3 w-3 text-muted-foreground/70 flex-shrink-0" />
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">{label}</span>
            </div>
            <span className="text-sm font-semibold text-foreground tabular-nums">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
